import { RotateCcw } from 'lucide-react'
import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Button } from '@/shared/ui/button'

type VersionRestoreButtonProps = {
  version: number
  onRestore: (version: number) => void
  isPending?: boolean
}

/**
 * Restaura el expediente a una versión anterior, detrás de una confirmación.
 *
 * ── POR QUÉ CONFIRMA ─────────────────────────────────────────────────────────
 *
 * Restaurar reescribe el expediente vivo y deshace el trabajo de otra persona. No es
 * irreversible —la restauración crea una versión nueva, así que se puede volver— pero sí es
 * visible para todo el equipo en el momento en que ocurre.
 *
 * El texto dice las TRES cosas que alguien necesita saber antes de apretar, y ninguna es
 * obvia desde la pantalla:
 *
 * 1. Que se puede deshacer, para que no dé miedo usarlo cuando corresponde.
 * 2. Que el estado del flujo NO se mueve, porque es lo primero que la gente asume que sí.
 * 3. Que los efectos ya ocurridos no se revierten, que es lo que podría llevar a alguien a
 *    creer que un correo enviado se «cancela» restaurando.
 */
export function VersionRestoreButton({ version, onRestore, isPending }: VersionRestoreButtonProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit gap-1.5"
        disabled={isPending}
        onClick={() => setIsOpen(true)}
      >
        <RotateCcw className="size-3.5" />
        Restaurar esta versión
      </Button>

      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Restaurar la versión {version}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  El expediente vuelve a como estaba en esa versión. Queda registrado quién lo
                  restauró, y se puede deshacer volviendo a la versión actual.
                </p>
                <p>
                  <strong className="font-medium">El estado del flujo no se mueve.</strong> Si el
                  expediente avanzó desde entonces, sigue donde está: para cambiarlo se usan las
                  transiciones.
                </p>
                <p className="text-muted-foreground">
                  Lo que ya ocurrió no se revierte: correos enviados, órdenes despachadas a SAP o
                  archivos eliminados siguen igual.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onRestore(version)
                setIsOpen(false)
              }}
            >
              Restaurar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
